/**
 * MovingAverageChart — raw concentration scatter + rolling 3/6/12-month
 * trend lines for a single well × analyte.
 *
 * Native hand-rolled SVG (no Recharts). Same recipe as BumpChart/StepChart:
 *   - d3-scale + d3-shape for axis + curve math
 *   - useChartView for rectangle zoom + dblclick reset
 *   - <ChartTimeRangeRow> for preset time-range selection
 *   - <ChartTooltip> on hover with raw + active MA values
 *   - Regulatory MCL lines rendered with the dv-mcl-glow filter
 */

import { useState, useMemo, useRef, useEffect, useCallback, useId } from 'react';
import { scaleTime, scaleLinear } from 'd3-scale';
import { line as d3Line, curveMonotoneX } from 'd3-shape';
import type { AnalyticalTimeseriesResponse, ConcentrationUnit } from '@/api/dataVault';
import { WELL_COLORS } from '../../chartTheme';
import { ChartTooltip } from '../../ChartTooltip';
import { useChartView } from '../../interactions/useChartView';
import { ChartTimeRangeRow } from '../../ChartTimeRangeRow';
import { useRegulatoryLines } from '../../../hooks/useRegulatoryLines';
import { defaultSelectedWellId } from '../../chartDefaults';

interface Props {
  data: AnalyticalTimeseriesResponse;
  unit: ConcentrationUnit;
}

function convertConc(val: number | null, _unit: ConcentrationUnit): number | null {
  if (val === null) return null;
  return val;
}

type WindowKey = '3m' | '6m' | '12m';

const WINDOW_CONFIG: Record<
  WindowKey,
  { months: number; label: string; color: string; dash: string }
> = {
  '3m':  { months: 3,  label: '3-Mo',  color: 'var(--dv-accent-cyan, #06b6d4)',    dash: '4 2' },
  '6m':  { months: 6,  label: '6-Mo',  color: 'var(--dv-accent-emerald, #10b981)', dash: '8 4' },
  '12m': { months: 12, label: '12-Mo', color: 'var(--dv-accent-amber, #f59e0b)',   dash: '' },
};

interface RawPoint {
  ts: number;
  value: number;
  ma3: number | null;
  ma6: number | null;
  ma12: number | null;
}

const MARGIN = { top: 12, right: 24, bottom: 36, left: 64 };
const CHART_HEIGHT = 420;

function computeRollingAverage(
  sorted: Array<{ ts: number; value: number }>,
  windowMonths: number,
): Array<number | null> {
  return sorted.map((pt) => {
    const end = pt.ts;
    const start = end - windowMonths * 30.5 * 86_400_000;
    let sum = 0;
    let count = 0;
    for (const p of sorted) {
      if (p.ts >= start && p.ts <= end) {
        sum += p.value;
        count++;
      }
    }
    return count === 0 ? null : sum / count;
  });
}

export function MovingAverageChart({ data, unit }: Props) {
  // ── Analyte + well selectors ─────────────────────────────────────────

  const allAnalytes = useMemo(() => {
    const map = new Map<string, string>();
    for (const well of data.wells) {
      for (const [code, analyte] of Object.entries(well.analytes)) {
        if (!map.has(code)) map.set(code, analyte.compound);
      }
    }
    return Array.from(map.entries()).map(([code, compound]) => ({ code, compound }));
  }, [data.wells]);

  const defaultAnalyte = useMemo(() => {
    const benzene = allAnalytes.find((a) => a.compound.toLowerCase() === 'benzene');
    return benzene?.code ?? allAnalytes[0]?.code ?? '';
  }, [allAnalytes]);

  const [selectedAnalyte, setSelectedAnalyte] = useState<string>(defaultAnalyte);

  const wellItems = useMemo(
    () => data.wells.map((w, i) => ({ well_id: w.well_id, well_name: w.well_name, color: WELL_COLORS[i % WELL_COLORS.length] })),
    [data.wells],
  );
  const [selectedWellId, setSelectedWellId] = useState<string>(() => defaultSelectedWellId(data.wells));
  const [activeWindows, setActiveWindows] = useState<Set<WindowKey>>(new Set(['3m', '6m', '12m']));

  const regVisibleCodes = useMemo(() => new Set([selectedAnalyte]), [selectedAnalyte]);
  const { lines: regulatoryLines } = useRegulatoryLines(data, regVisibleCodes);

  // ── Build chart data (raw + MAs) ─────────────────────────────────────

  const chartData: RawPoint[] = useMemo(() => {
    const well = data.wells.find((w) => w.well_id === selectedWellId);
    if (!well) return [];
    const analyteData = well.analytes[selectedAnalyte];
    if (!analyteData) return [];

    const sorted = analyteData.measurements
      .filter((m) => m.concentration !== null)
      .map((m) => ({ ts: new Date(m.date).getTime(), value: convertConc(m.concentration, unit)! }))
      .sort((a, b) => a.ts - b.ts);

    if (sorted.length === 0) return [];

    const ma3 = computeRollingAverage(sorted, 3);
    const ma6 = computeRollingAverage(sorted, 6);
    const ma12 = computeRollingAverage(sorted, 12);

    return sorted.map((pt, i) => ({
      ts: pt.ts,
      value: pt.value,
      ma3: ma3[i],
      ma6: ma6[i],
      ma12: ma12[i],
    }));
  }, [data.wells, selectedWellId, selectedAnalyte, unit]);

  // ── Responsive width + refs ─────────────────────────────────────────

  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [width, setWidth] = useState(800);
  const clipId = `ma-clip-${useId().replace(/:/g, '')}`;

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (typeof w === 'number' && w > 0) setWidth(Math.round(w));
    });
    ro.observe(el);
    setWidth(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, []);

  const innerWidth = Math.max(0, width - MARGIN.left - MARGIN.right);
  const innerHeight = Math.max(0, CHART_HEIGHT - MARGIN.top - MARGIN.bottom);

  // ── Base domains ────────────────────────────────────────────────────

  const baseXDomain = useMemo<readonly [number, number]>(() => {
    if (chartData.length === 0) return [0, 1];
    if (chartData.length === 1) {
      const t = chartData[0].ts;
      return [t - 86_400_000, t + 86_400_000];
    }
    return [chartData[0].ts, chartData[chartData.length - 1].ts];
  }, [chartData]);

  const baseYDomain = useMemo<readonly [number, number]>(() => {
    let yMax = 0;
    for (const p of chartData) {
      if (p.value > yMax) yMax = p.value;
    }
    for (const line of regulatoryLines) {
      if (line.value > yMax) yMax = line.value;
    }
    return [0, (yMax > 0 ? yMax : 1) * 1.1];
  }, [chartData, regulatoryLines]);

  // ── View hook ────────────────────────────────────────────────────────

  const xPxToDataRef = useRef<(px: number) => number>(() => 0);
  const yPxToDataRef = useRef<(py: number) => number>(() => 0);

  const view = useChartView(
    svgRef,
    (px) => xPxToDataRef.current(px),
    (py) => yPxToDataRef.current(py),
    {
      baseXDomain,
      baseYDomain,
      dragZoomAxes: 'x',
      chartArea: {
        top: MARGIN.top,
        bottom: MARGIN.top + innerHeight,
        left: MARGIN.left,
        right: MARGIN.left + innerWidth,
      },
    },
  );

  const xScale = useMemo(
    () => scaleTime().domain([view.xDomain[0], view.xDomain[1]]).range([MARGIN.left, MARGIN.left + innerWidth]),
    [view.xDomain, innerWidth],
  );
  const yScale = useMemo(
    () => scaleLinear().domain([view.yDomain[0], view.yDomain[1]]).range([MARGIN.top + innerHeight, MARGIN.top]).nice(),
    [view.yDomain, innerHeight],
  );

  xPxToDataRef.current = (px: number) => xScale.invert(px).getTime();
  yPxToDataRef.current = (py: number) => yScale.invert(py);

  // ── Line generators ─────────────────────────────────────────────────

  const lineGenMa3 = useMemo(
    () =>
      d3Line<RawPoint>()
        .defined((p) => p.ma3 !== null)
        .x((p) => xScale(p.ts))
        .y((p) => yScale(p.ma3 as number))
        .curve(curveMonotoneX),
    [xScale, yScale],
  );
  const lineGenMa6 = useMemo(
    () =>
      d3Line<RawPoint>()
        .defined((p) => p.ma6 !== null)
        .x((p) => xScale(p.ts))
        .y((p) => yScale(p.ma6 as number))
        .curve(curveMonotoneX),
    [xScale, yScale],
  );
  const lineGenMa12 = useMemo(
    () =>
      d3Line<RawPoint>()
        .defined((p) => p.ma12 !== null)
        .x((p) => xScale(p.ts))
        .y((p) => yScale(p.ma12 as number))
        .curve(curveMonotoneX),
    [xScale, yScale],
  );

  // ── Ticks ──────────────────────────────────────────────────────────

  const xTicks = useMemo(() => xScale.ticks(6), [xScale]);
  const yTicks = useMemo(() => yScale.ticks(5), [yScale]);

  const formatX = (d: Date) =>
    `${d.getMonth() + 1}/${d.getFullYear().toString().slice(2)}`;
  const formatY = (v: number) => {
    if (v === 0) return '0';
    const abs = Math.abs(v);
    if (abs < 0.01) return v.toExponential(1);
    if (abs < 1) return v.toPrecision(2);
    return v.toFixed(1);
  };

  // ── Hover ──────────────────────────────────────────────────────────

  interface HoverState { point: RawPoint; clientX: number; clientY: number }
  const [hover, setHover] = useState<HoverState | null>(null);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<SVGRectElement>) => {
      const svg = svgRef.current; if (!svg) return;
      const rect = svg.getBoundingClientRect();
      const localX = e.clientX - rect.left;
      const localY = e.clientY - rect.top;
      let best: HoverState | null = null;
      let bestDistSq = 14 * 14;
      for (const p of chartData) {
        const px = xScale(p.ts);
        const py = yScale(p.value);
        const dx = px - localX;
        const dy = py - localY;
        const dsq = dx * dx + dy * dy;
        if (dsq < bestDistSq) {
          bestDistSq = dsq;
          best = { point: p, clientX: e.clientX, clientY: e.clientY };
        }
      }
      setHover(best);
    },
    [chartData, xScale, yScale],
  );
  const handleMouseLeave = useCallback(() => setHover(null), []);

  const toggleWindow = (key: WindowKey) => {
    setActiveWindows((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (!data.wells.length) {
    return (
      <div className="flex items-center justify-center py-16 text-mono-600 text-sm tracking-wider">
        NO ANALYTICAL DATA AVAILABLE
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-xs mb-3" style={{ color: 'var(--dv-text-muted)' }}>
        Moving average chart overlaying raw sample data with a smoothed rolling average. Toggle 3, 6,
        or 12-month windows to reveal underlying trends beneath sampling noise.
      </p>

      <div className="flex flex-wrap items-center gap-3">
        <select
          value={selectedAnalyte}
          onChange={(e) => setSelectedAnalyte(e.target.value)}
          className="rounded-lg border px-2 py-1 text-xs"
          style={{ background: 'var(--dv-bg-card)', borderColor: 'var(--dv-border-subtle)', color: 'var(--dv-text-primary)' }}
        >
          {allAnalytes.map((a) => (
            <option key={a.code} value={a.code}>{a.compound}</option>
          ))}
        </select>

        <select
          value={selectedWellId}
          onChange={(e) => setSelectedWellId(e.target.value)}
          className="rounded-lg border px-2 py-1 text-xs"
          style={{ background: 'var(--dv-bg-card)', borderColor: 'var(--dv-border-subtle)', color: 'var(--dv-text-primary)' }}
        >
          {wellItems.map((w) => (
            <option key={w.well_id} value={w.well_id}>{w.well_name}</option>
          ))}
        </select>

        <div className="flex gap-1.5 ml-auto">
          {(Object.keys(WINDOW_CONFIG) as WindowKey[]).map((key) => {
            const cfg = WINDOW_CONFIG[key];
            const isActive = activeWindows.has(key);
            return (
              <button
                key={key}
                onClick={() => toggleWindow(key)}
                className="rounded-full px-3 py-0.5 text-[10px] font-medium tracking-wider border transition-colors"
                style={{
                  background: isActive ? 'var(--dv-pill-active-bg)' : 'var(--dv-pill-bg)',
                  borderColor: isActive ? cfg.color : 'var(--dv-border-subtle)',
                  color: isActive ? cfg.color : 'var(--dv-text-secondary)',
                }}
              >
                {cfg.label}
              </button>
            );
          })}
        </div>
      </div>

      <ChartTimeRangeRow
        dataMinMs={baseXDomain[0]}
        dataMaxMs={baseXDomain[1]}
        activeDomain={view.xDomain}
        onSelect={(d) => view.setXDomain(d)}
        isZoomed={view.isZoomed}
        onReset={view.reset}
      />

      <div ref={containerRef} role="img" aria-label="Moving average chart of concentration trends">
        {chartData.length === 0 ? (
          <div className="flex items-center justify-center py-16 text-mono-600 text-sm tracking-wider">
            NO ANALYTICAL DATA AVAILABLE
          </div>
        ) : (
          <svg
            ref={svgRef}
            width={width || '100%'}
            height={CHART_HEIGHT}
            style={{ display: 'block', userSelect: 'none', touchAction: 'none', cursor: 'crosshair' }}
          >
            <defs>
              <clipPath id={clipId}>
                <rect x={MARGIN.left} y={MARGIN.top} width={innerWidth} height={innerHeight} />
              </clipPath>
            </defs>

            <g clipPath={`url(#${clipId})`}>
            {/* Gridlines */}
            {yTicks.map((t) => {
              const y = yScale(t);
              return (
                <line
                  key={`grid-${t}`}
                  x1={MARGIN.left}
                  x2={MARGIN.left + innerWidth}
                  y1={y}
                  y2={y}
                  stroke="var(--dv-chart-grid)"
                  strokeDasharray="3 3"
                  strokeWidth={1}
                  shapeRendering="crispEdges"
                />
              );
            })}

            {/* Regulatory lines */}
            {regulatoryLines.map((line) => {
              const v = convertConc(line.value, unit);
              if (v === null) return null;
              const [yLo, yHi] = view.yDomain;
              if (v > yHi || v < yLo) return null;
              const y = yScale(v);
              const dashArray = line.style === 'dashed' ? '8 4' : line.style === 'dotted' ? '2 4' : undefined;
              return (
                <g key={line.key} style={{ filter: 'url(#dv-mcl-glow)' }}>
                  <line
                    x1={MARGIN.left}
                    x2={MARGIN.left + innerWidth}
                    y1={y}
                    y2={y}
                    stroke={line.color}
                    strokeWidth={1}
                    strokeDasharray={dashArray}
                  />
                  <text
                    x={MARGIN.left + innerWidth - 4}
                    y={y - 4}
                    textAnchor="end"
                    fontSize={9}
                    fill={line.color}
                    style={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {`${line.label} ${line.analyteName}`}
                  </text>
                </g>
              );
            })}

            {/* MA lines */}
            {activeWindows.has('12m') && (
              <path d={lineGenMa12(chartData) ?? ''} fill="none" stroke={WINDOW_CONFIG['12m'].color} strokeWidth={2.5} strokeLinejoin="round" />
            )}
            {activeWindows.has('6m') && (
              <path d={lineGenMa6(chartData) ?? ''} fill="none" stroke={WINDOW_CONFIG['6m'].color} strokeWidth={2.5} strokeDasharray={WINDOW_CONFIG['6m'].dash} strokeLinejoin="round" />
            )}
            {activeWindows.has('3m') && (
              <path d={lineGenMa3(chartData) ?? ''} fill="none" stroke={WINDOW_CONFIG['3m'].color} strokeWidth={2} strokeDasharray={WINDOW_CONFIG['3m'].dash} strokeLinejoin="round" />
            )}

            {/* Raw scatter */}
            {chartData.map((p, i) => {
              const isHovered = hover?.point.ts === p.ts;
              return (
                <circle
                  key={`raw-${i}`}
                  cx={xScale(p.ts)}
                  cy={yScale(p.value)}
                  r={isHovered ? 5 : 3.5}
                  fill={WELL_COLORS[0]}
                  stroke="var(--dv-bg-card)"
                  strokeWidth={isHovered ? 2 : 1}
                  style={isHovered ? { filter: 'url(#dv-hover-halo)' } : undefined}
                />
              );
            })}
            </g>

            {/* Y-axis */}
            <line x1={MARGIN.left} x2={MARGIN.left} y1={MARGIN.top} y2={MARGIN.top + innerHeight} stroke="var(--dv-chart-axis)" strokeWidth={1} />
            {yTicks.map((t) => {
              const y = yScale(t);
              return (
                <g key={`yt-${t}`}>
                  <line x1={MARGIN.left - 4} x2={MARGIN.left} y1={y} y2={y} stroke="var(--dv-chart-axis)" strokeWidth={1} />
                  <text x={MARGIN.left - 8} y={y} textAnchor="end" dominantBaseline="middle" fontSize={11} fill="var(--dv-chart-axis)" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatY(t)}
                  </text>
                </g>
              );
            })}
            <text transform={`translate(${MARGIN.left - 48}, ${MARGIN.top + innerHeight / 2}) rotate(-90)`} textAnchor="middle" fontSize={11} fill="var(--dv-chart-axis)">
              Concentration (mg/L)
            </text>

            {/* X-axis */}
            <line x1={MARGIN.left} x2={MARGIN.left + innerWidth} y1={MARGIN.top + innerHeight} y2={MARGIN.top + innerHeight} stroke="var(--dv-chart-axis)" strokeWidth={1} />
            {xTicks.map((t, i) => {
              const x = xScale(t);
              return (
                <g key={`xt-${i}`}>
                  <line x1={x} x2={x} y1={MARGIN.top + innerHeight} y2={MARGIN.top + innerHeight + 4} stroke="var(--dv-chart-axis)" strokeWidth={1} />
                  <text x={x} y={MARGIN.top + innerHeight + 16} textAnchor="middle" fontSize={11} fill="var(--dv-chart-axis)" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatX(t)}
                  </text>
                </g>
              );
            })}

            {/* Rect-zoom preview */}
            {view.rectPreview && (
              <rect
                x={view.rectPreview.x0}
                y={view.rectPreview.y0}
                width={view.rectPreview.x1 - view.rectPreview.x0}
                height={view.rectPreview.y1 - view.rectPreview.y0}
                fill="rgba(16, 185, 129, 0.12)"
                stroke="var(--dv-accent-emerald, #10b981)"
                strokeWidth={1}
                strokeDasharray="3 3"
                pointerEvents="none"
              />
            )}

            {/* Hit overlay */}
            <rect
              x={MARGIN.left}
              y={MARGIN.top}
              width={innerWidth}
              height={innerHeight}
              fill="transparent"
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
            />
          </svg>
        )}
      </div>

      {hover && (
        <ChartTooltip visible x={hover.clientX} y={hover.clientY} placement="top">
          <div style={{ minWidth: 180 }}>
            <div
              style={{
                fontSize: 10,
                letterSpacing: '0.04em',
                color: 'var(--dv-tooltip-label, rgba(255,255,255,0.7))',
                textTransform: 'uppercase',
                marginBottom: 4,
                paddingBottom: 4,
                borderBottom: '1px solid var(--dv-tooltip-border, rgba(255,255,255,0.08))',
              }}
            >
              {new Date(hover.point.ts).toLocaleDateString()}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 11 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                <span style={{ color: 'var(--dv-tooltip-label, rgba(255,255,255,0.7))' }}>Raw</span>
                <span style={{ fontFamily: 'var(--dv-font-mono, ui-monospace, monospace)', fontWeight: 600 }}>
                  {hover.point.value.toFixed(4)} mg/L
                </span>
              </div>
              {(['3m', '6m', '12m'] as WindowKey[]).map((key) => {
                if (!activeWindows.has(key)) return null;
                const v = hover.point[`ma${WINDOW_CONFIG[key].months}` as keyof RawPoint] as number | null;
                if (v === null) return null;
                return (
                  <div key={key} style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                    <span style={{ color: WINDOW_CONFIG[key].color }}>{WINDOW_CONFIG[key].label}</span>
                    <span style={{ fontFamily: 'var(--dv-font-mono, ui-monospace, monospace)' }}>
                      {v.toFixed(4)} mg/L
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </ChartTooltip>
      )}
    </div>
  );
}
